import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import {
  getV2ApiCompanyIdTransferLimitsInstantPaymentLimits,
  postV2ApiCompanyIdTransferLimitsInstantPaymentLimits,
} from 'src/client/spendesk-api';
import type { GetV2ApiCompanyIdTransferLimitsInstantPaymentLimitsResponseOK } from 'src/client/spendesk-api-types';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

export type InstantPaymentLimitsResult =
  GetV2ApiCompanyIdTransferLimitsInstantPaymentLimitsResponseOK;

export const instantPaymentLimitsQueryKey = (companyId: string) => [
  'companyInstantPaymentLimits',
  companyId,
];

export const fetchInstantPaymentLimits = async (
  companyId: string,
): Promise<InstantPaymentLimitsResult> => {
  const response = await getV2ApiCompanyIdTransferLimitsInstantPaymentLimits({
    path: {
      companyId,
    },
  });

  if (response.statusCode !== 200) {
    throw new Error('Failed to fetch instant payment limits');
  }

  return response.body;
};

export const updateInstantPaymentLimits = async ({
  amountLimit,
  dailyLimit,
  companyId,
}: {
  amountLimit?: number;
  dailyLimit?: number | null;
  companyId: string;
}) => {
  const response = await postV2ApiCompanyIdTransferLimitsInstantPaymentLimits({
    path: {
      companyId,
    },
    body: {
      amountLimit,
      dailyLimit,
    },
  });
  if (response.statusCode !== 201) {
    throw new Error('Failed to update instant payment limits');
  }
  return response.body;
};

export const setCompanyInstantPaymentLimitsQueryData = (
  queryClient: QueryClient,
  companyId: string,
  updater: (
    previousData:
      | GetV2ApiCompanyIdTransferLimitsInstantPaymentLimitsResponseOK
      | undefined,
  ) =>
    | GetV2ApiCompanyIdTransferLimitsInstantPaymentLimitsResponseOK
    | undefined,
) => {
  queryClient.setQueryData<
    GetV2ApiCompanyIdTransferLimitsInstantPaymentLimitsResponseOK | undefined
  >(instantPaymentLimitsQueryKey(companyId), updater);
};

type UseCompanyInstantPaymentLimitsQueryProps = {
  isEnabled: boolean;
};

export const useCompanyInstantPaymentLimitsQuery = ({
  isEnabled,
}: UseCompanyInstantPaymentLimitsQueryProps) => {
  const companyId = useCompanyId();

  return useQuery<InstantPaymentLimitsResult>(
    instantPaymentLimitsQueryKey(companyId),
    () => fetchInstantPaymentLimits(companyId),
    {
      enabled: isEnabled,
    },
  );
};

export const useUpdateInstantPaymentDailyLimits = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ dailyLimit }: { dailyLimit: number | null }) => {
      return updateInstantPaymentLimits({ dailyLimit, companyId });
    },
    {
      onSuccess: (_, payload) => {
        return setCompanyInstantPaymentLimitsQueryData(
          queryClient,
          companyId,
          (previousData) => {
            if (!previousData) {
              return previousData;
            }
            return {
              ...previousData,
              dailyLimit:
                payload.dailyLimit === null ? undefined : payload.dailyLimit,
            };
          },
        );
      },
    },
  );
};

export const useUpdateInstantPaymentAmountLimits = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      amountLimit,
      disableDailyLimit,
    }: {
      amountLimit: number;
      disableDailyLimit?: boolean;
    }) => {
      return updateInstantPaymentLimits({
        amountLimit,
        dailyLimit: disableDailyLimit ? null : undefined,
        companyId,
      });
    },
    {
      onSuccess: (_, payload) => {
        return setCompanyInstantPaymentLimitsQueryData(
          queryClient,
          companyId,
          (previousData) => {
            if (!previousData) {
              return previousData;
            }
            return {
              ...previousData,
              amountLimit: payload.amountLimit,
              ...(payload.disableDailyLimit ? { dailyLimit: undefined } : {}),
            };
          },
        );
      },
    },
  );
};
